import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Group, Loader, LoadingOverlay, Text } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { flexRender } from '@tanstack/react-table';
import type {
  CellContext,
  ColumnDef,
  ExpandedState,
  Row,
  RowSelectionState,
} from '@tanstack/react-table';
import RTable from '@ayocore/platform/client/components/RTable/RTable';
import type {
  AggregateItem,
  AggregateResult,
  GroupRow,
} from '@ayocore/platform/common/types/aggregate';
import { useRepositorySelector, useStoreSelector } from './useRepositoryController';
import type { RepositoryController } from './RepositoryController';
import { GroupsDataSource } from './GroupsDataSource';
import { PAGE_SIZE_OPTIONS, TREE_DEPTH_INDENT } from './constants';
import { createRowKeyResolver } from './rowKey';
import type { RepositoryTableGroupBy } from './types';

/** Фон строки-итога: отличает её от строк сущностей. */
const GROUP_ROW_BACKGROUND = '#f6f8fa';


const numberFormatter = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

/**
 * Строка дерева: либо строка-итог группы (`__group`), либо сущность.
 *
 * Тип пересечения с `T`, а не union: так колонки сущности (`ColumnDef<T>`)
 * остаются пригодными без переписывания — их `accessorKey`/`cell` работают с
 * дочерними строками как обычно.
 */
type GroupedNode<T> = T & {
  /** Заполнено только у строки-итога */
  __group?: GroupRow;
  /** Строки группы (лениво подгруженные) */
  __children?: GroupedNode<T>[];
};

export interface GroupedTableWrapperProps<T extends Record<string, unknown>> {
  /**
   * id таблицы — ТОТ ЖЕ, что у обычного режима.
   *
   * Общий стор настроек здесь обязателен: групповой вид рисует те же колонки
   * сущности, их переставляют тем же drag'ом и настраивают той же модалкой
   * «Колонки». С отдельным id порядок, заданный мышью в режиме групп, расходился
   * бы с тем, что показывает модалка (она читает стор обычного вида).
   *
   * Лишние id (колонки агрегатов, которых нет в плоском виде) в сторе не мешают:
   * порядок примиряется с фактическим набором колонок при каждом рендере.
   */
  id: string;
  controller: RepositoryController<T>;
  groupBy: RepositoryTableGroupBy<T>;
  /** Колонки сущности — ими рисуются дочерние строки */
  columns: ColumnDef<T>[];
  /** Разрешить выбор дочерних строк (включается при наличии bulkActions) */
  enableChildSelection?: boolean;
  /**
   * Ключ дочерней строки — тот же проп `getRowId`, что у обычного режима.
   * Обязан совпадать: под этим ключом выбор лежит в контроллере, и по нему же
   * bulkActions разрешают выбранные строки.
   */
  getRowId?: (row: T, index: number) => string;
  /**
   * Пропсы строки от потребителя. Применяются ТОЛЬКО к дочерним строкам:
   * строка-итог не сущность, и раскрашивать её по полям `T` нечем — у неё
   * собственный фон.
   */
  getRowProps?: (row: Row<T>) => React.HTMLAttributes<HTMLDivElement>;
  /** Клик по дочерней строке. По строке-итогу клик раскрывает группу. */
  onRowClick?: (row: Row<T>, rowData: T) => void;
  /** Двойной клик по дочерней строке. */
  onRowDoubleClick?: (row: Row<T>, rowData: T) => void;
  /** Какие дочерние строки можно выбирать (итоги не выбираются никогда). */
  isRowSelectable?: (row: Row<T>) => boolean;
  className?: string;
  rowHeight?: number;
  bodyHeight?: number | string;
  withColumnBorders?: boolean | 'header';
  showBottom?: boolean;
  emptyPlaceholder?: React.ReactNode;
  /** Нижние агрегаты — считаются по всей выборке и от группировки не зависят */
  aggregateData?: AggregateResult | null;
  aggregateConfig?: AggregateItem<T>[];
  aggregatesLoading?: boolean;
}

/**
 * Заголовок группы: шеврон, подпись, счётчик и состояние догрузки.
 *
 * Отдельный компонент, подписанный на состояние СВОЕЙ группы: если читать
 * загрузку/ошибку из замыкания, карта детей попадает в зависимости колонок, и
 * каждая догрузка пересобирает `ColumnDef` всей таблицы (перерисовка всего RTable).
 */
function GroupTitleCell<T extends Record<string, unknown>>({
  row,
  group,
  groupsSource,
  renderValue,
  onToggle,
}: {
  row: Row<GroupedNode<T>>;
  group: GroupRow;
  groupsSource: GroupsDataSource<T>;
  renderValue?: (group: GroupRow) => React.ReactNode;
  onToggle: (value: GroupRow['value']) => void;
}) {
  const key = GroupsDataSource.childrenKey(group.value);
  const children = useStoreSelector(groupsSource, s => s.children[key]);
  const expanded = row.getIsExpanded();
  const Chevron = expanded ? IconChevronDown : IconChevronRight;

  // Пустая строка и NULL — разные группы (у них разные ключи), поэтому и подписи
  // разные: иначе в списке две визуально одинаковые строки-итога.
  const label =
    renderValue?.(group) ??
    (group.value == null
      ? '— не указано —'
      : group.value === ''
        ? '— пустая строка —'
        : String(group.value));

  return (
    <Group
      gap={6}
      wrap='nowrap'
      style={{ cursor: 'pointer' }}
      // Клик по строке-итогу тоже раскрывает группу (обработчик строки), поэтому
      // здесь событие останавливаем: иначе два переключения подряд дали бы ноль.
      onClick={event => {
        event.stopPropagation();
        onToggle(group.value);
      }}
    >
      <Chevron size={14} />
      <Text size='xs' fw={600} truncate>
        {label}
      </Text>
      <Badge size='xs' variant='light' color='gray'>
        {group.count}
      </Badge>
      {children?.loading && <Loader size={10} />}
      {children?.truncated && (
        <Text size='xs' c='dimmed'>
          (показаны первые {children.rows.length})
        </Text>
      )}
      {children?.error && (
        <Text size='xs' c='red' truncate>
          {children.error}
        </Text>
      )}
    </Group>
  );
}

/** Число в стиле итога: вправо, русский формат. */
function GroupNumberCell({ value, bold }: { value: unknown; bold?: boolean }) {
  if (value == null) return null;
  return (
    <Text size='xs' ta='right' w='100%' fw={bold ? 600 : 400}>
      {numberFormatter.format(Number(value))}
    </Text>
  );
}

/**
 * Табличный вид режима группировки: строки-итоги считает сервер (`aggregateBy`),
 * раскрытие догружает строки группы и показывает их дочерними строками ТОГО ЖЕ
 * инстанса таблицы — поэтому колонки и их ширины у итога и у строк совпадают
 * по построению (ширина живёт на колонке, а не на строке).
 *
 * Отдельный компонент, а не ветка внутри RepositoryTableWrapper: у таблицы групп
 * свой набор колонок и свой id для персиста настроек — общий id смешал бы
 * колонки групп с колонками сущности.
 */
export function GroupedTableWrapper<T extends Record<string, unknown>>({
  id,
  controller,
  groupBy,
  columns,
  enableChildSelection = false,
  getRowId: externalGetRowId,
  getRowProps,
  onRowClick,
  onRowDoubleClick,
  isRowSelectable,
  className,
  rowHeight,
  bodyHeight,
  withColumnBorders,
  showBottom = true,
  emptyPlaceholder,
  aggregateData,
  aggregateConfig,
  aggregatesLoading,
}: GroupedTableWrapperProps<T>) {
  // Группы и подгруженные дети живут в своём сторе (GroupsDataSource), а
  // страница/выбор/загрузка списка — в контроллере: подписываемся на оба.
  const groupsSource = controller.getGroupsSource();
  const groups = useStoreSelector(groupsSource, s => s.groups);
  const groupChildren = useStoreSelector(groupsSource, s => s.children);
  const groupsVersion = useStoreSelector(groupsSource, s => s.version);
  const total = useRepositorySelector(controller, s => s.totalCount);
  const isLoading = useRepositorySelector(controller, s => s.isLoading);
  const selectedIds = useRepositorySelector(controller, s => s.selectedIds);

  // Выделение берём напрямую из контроллера — он единственный владелец выбора
  // (переживает рефреш и localStorage). Своего стора у таблицы групп быть не
  // должно: иначе у группового и обычного вида были бы разные галочки.
  const rowSelection = useMemo(
    () => Object.fromEntries(selectedIds.map(id => [id, true])),
    [selectedIds]
  );
  const { page, pageSize } = controller.getQuery();

  // Раскрытием владеет этот компонент, а не TanStack: догруженные дети живут в
  // сторе групп и сбрасываются при каждой загрузке страницы групп. Если бы
  // раскрытие жило внутри таблицы, после обновления строка осталась бы
  // «раскрытой», детей под ней уже нет, а повторную догрузку никто не запускает.
  //
  // Сброс привязан к НОМЕРУ загрузки, а не к содержимому групп: обновление с
  // теми же итогами даёт равный по значению массив групп (селектор специально
  // сохраняет ссылку), и по нему сброс бы не сработал.
  const [expanded, setExpanded] = useState<ExpandedState>({});
  useEffect(() => setExpanded({}), [groupsVersion]);

  // Проп `groupBy` почти всегда объект-литерал в JSX, поэтому ниже всё завязано
  // не на его identity, а на стабильные части: примитивы, набор агрегатов по
  // подписи и функцию подписи через ref. Иначе колонки (а с ними весь RTable)
  // пересобирались бы на каждый рендер родителя.
  const groupField = String(groupBy.field);
  const groupLabel = groupBy.label;
  const aggregatesSignature = JSON.stringify(groupBy.aggregates ?? []);
  const aggregates = useMemo<AggregateItem<T>[]>(
    () => groupBy.aggregates ?? [],
    [aggregatesSignature]
  );

  // renderValue — функция-литерал: её identity меняется каждый рендер. Держим
  // последнюю версию в ref, а наружу отдаём стабильную обёртку.
  const renderValueRef = useRef(groupBy.renderValue);
  renderValueRef.current = groupBy.renderValue;
  const renderValue = useCallback(
    (group: GroupRow) => renderValueRef.current?.(group),
    []
  );

  /**
   * field → алиас агрегата: строка-итог показывает агрегат в колонке своего поля.
   * Если на одно поле задано несколько агрегатов, колонку сущности занимает
   * первый — остальные получат собственные колонки (ниже), а не пропадут молча.
   */
  const aliasByField = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of aggregates) {
      const field = String(item.field);
      if (!map.has(field)) map.set(field, `${item.fn}_${item.field}`);
    }
    return map;
  }, [aggregates]);

  /** Дерево: строки-итоги + подгруженные дети. */
  const data = useMemo<GroupedNode<T>[]>(
    () =>
      groups.map(group => {
        const children = groupChildren[GroupsDataSource.childrenKey(group.value)];
        // Узел итога не является сущностью T — полей у неё нет. TanStack требует
        // один тип строки на инстанс, поэтому итог собирается пустым объектом с
        // пометкой __group (единственное приведение в этом компоненте).
        const node = { __group: group, __children: children?.rows } as GroupedNode<T>;
        return node;
      }),
    [groups, groupChildren]
  );

  /** Колонка ключа: в ней живёт шеврон и имя группы. */
  const groupColumnId = useMemo(
    () => (columns.some(c => c.id === groupField) ? groupField : null),
    [columns, groupField]
  );

  /** Клик по группе: переключить раскрытие и догрузить строки, если ещё не грузили. */
  const toggleGroup = useCallback(
    (row: Row<GroupedNode<T>>, value: GroupRow['value']) => {
      row.toggleExpanded();
      // Сворачивание сюда тоже попадает — повторный вызов безвреден (загрузка
      // идемпотентна), а отдельная ветка ради этого не оправдана.
      void controller.loadGroupChildren(value);
    },
    [controller]
  );

  const columnsWithGroups = useMemo<ColumnDef<GroupedNode<T>>[]>(() => {
    const renderGroupTitle = (row: Row<GroupedNode<T>>, group: GroupRow) => (
      <GroupTitleCell<T>
        row={row}
        group={group}
        groupsSource={groupsSource}
        renderValue={renderValue}
        onToggle={value => toggleGroup(row, value)}
      />
    );

    const wrapped = columns.map<ColumnDef<GroupedNode<T>>>(column => ({
      ...column,
      cell: (ctx: CellContext<GroupedNode<T>, unknown>) => {
        const group = ctx.row.original.__group;

        // Дочерняя строка — обычная сущность: рисуем её штатной ячейкой колонки,
        // только со сдвигом в ключевой колонке, чтобы читалась вложенность.
        if (!group) {
          // У колонки может не быть своей `cell` (только accessorKey) — обычно
          // её отрисовал бы defaultColumn таблицы, но мы ячейку переопределяем,
          // и без фолбэка такая колонка у детей была бы пустой.
          const content = column.cell
            ? flexRender(column.cell, ctx)
            : String(ctx.getValue() ?? '');
          return column.id === groupColumnId ? (
            <div style={{ paddingLeft: TREE_DEPTH_INDENT * ctx.row.depth }}>{content}</div>
          ) : (
            content
          );
        }

        if (column.id === groupColumnId) return renderGroupTitle(ctx.row, group);

        // Строка-итог: в колонке с агрегатом — агрегат группы, в остальных пусто.
        const alias = column.id ? aliasByField.get(column.id) : undefined;
        return alias ? <GroupNumberCell value={group[alias]} bold /> : null;
      },
    }));

    // Агрегаты, которым не досталось колонки сущности (поле скрыто или на поле
    // задано несколько функций), получают свою колонку — иначе значение
    // посчитано сервером, но нигде не показано.
    const consumed = new Set(
      columns.map(c => (c.id ? aliasByField.get(c.id) : undefined)).filter(Boolean)
    );
    const extraAggregateColumns = aggregates
      .map(item => ({ item, alias: `${item.fn}_${item.field}` }))
      .filter(({ alias }) => !consumed.has(alias))
      .map<ColumnDef<GroupedNode<T>>>(({ item, alias }) => ({
        id: alias,
        header: item.label ?? alias,
        size: 130,
        cell: (ctx: CellContext<GroupedNode<T>, unknown>) => {
          const group = ctx.row.original.__group;
          return group ? <GroupNumberCell value={group[alias]} bold /> : null;
        },
      }));

    const body = [...wrapped, ...extraAggregateColumns];

    if (groupColumnId) return body;

    // Ключа среди колонок сущности нет (скрыт или это FK) — добавляем свою
    // колонку слева, иначе группу нечем подписать.
    const keyColumn: ColumnDef<GroupedNode<T>> = {
      id: '__group_key',
      header: groupLabel ?? groupField,
      size: 320,
      cell: (ctx: CellContext<GroupedNode<T>, unknown>) => {
        const group = ctx.row.original.__group;
        if (!group) return <div style={{ paddingLeft: TREE_DEPTH_INDENT * ctx.row.depth }} />;
        return renderGroupTitle(ctx.row, group);
      },
    };
    return [keyColumn, ...body];
    // Зависимости — только стабильные: identity пропа `groupBy` и карта детей
    // сюда попадать не должны (первое пересобирало бы колонки на каждый рендер
    // родителя, второе — на каждую догрузку строк группы).
  }, [
    columns,
    groupColumnId,
    groupField,
    groupLabel,
    aggregates,
    aliasByField,
    groupsSource,
    toggleGroup,
    renderValue,
  ]);

  /** Ключ дочерней строки — тот же резолвер, что у обычного режима. */
  const resolveEntityKey = useMemo(
    () => createRowKeyResolver<GroupedNode<T>>(externalGetRowId),
    [externalGetRowId]
  );

  /**
   * Ключ строки: у итога — по значению группы, у сущности — ключ таблицы.
   * Ключ итога обязан быть стабильным, иначе раскрытие слетало бы при догрузке.
   */
  const getRowId = useCallback(
    (node: GroupedNode<T>, index: number) => {
      const group = node.__group;
      if (group) return `group:${GroupsDataSource.childrenKey(group.value)}`;
      return resolveEntityKey(node, index);
    },
    [resolveEntityKey]
  );

  const getSubRows = useCallback((node: GroupedNode<T>) => node.__children, []);

  /**
   * Пропсы строки: у итога — свой фон, у ребёнка — то, что дал потребитель.
   * Вызывать его `getRowProps` для итога нельзя: он читает поля сущности,
   * которых у строки-итога нет.
   */
  const resolveRowProps = useCallback(
    (row: Row<GroupedNode<T>>) => {
      if (row.original.__group) return { style: { backgroundColor: GROUP_ROW_BACKGROUND } };
      return getRowProps?.(row) ?? {};
    },
    [getRowProps]
  );

  /** Клик по дочерней строке уходит потребителю; по итогу — раскрывает группу. */
  const handleRowClick = useCallback(
    (row: Row<GroupedNode<T>>, rowData: GroupedNode<T>) => {
      const group = row.original.__group;
      if (group) {
        toggleGroup(row, group.value);
        return;
      }
      onRowClick?.(row, rowData);
    },
    [onRowClick, toggleGroup]
  );

  const handleRowDoubleClick = useCallback(
    (row: Row<GroupedNode<T>>, rowData: GroupedNode<T>) => {
      if (row.original.__group) return;
      onRowDoubleClick?.(row, rowData);
    },
    [onRowDoubleClick]
  );

  // Выбирать можно только дочерние строки: у итога нет бизнес-id, а чекбокс на
  // группе отметил бы лишь подгруженную часть и вводил бы в заблуждение.
  // Предикат потребителя сужает выбор дальше — как в обычном режиме.
  const canSelectRow = useCallback(
    (row: Row<GroupedNode<T>>) => {
      if (row.original.__group) return false;
      return isRowSelectable?.(row) ?? true;
    },
    [isRowSelectable]
  );

  const handleRowSelectionChange = useCallback(
    (updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
      const next =
        typeof updater === 'function' ? updater(controller.getRowSelection()) : updater;
      controller.setRowSelection(next);
    },
    [controller]
  );

  return (
    <div className={className} style={{ position: 'relative' }}>
      <LoadingOverlay
        visible={isLoading}
        zIndex={50}
        overlayProps={{ radius: 'sm', blur: 1, backgroundOpacity: 0.35 }}
        loaderProps={{ size: 'sm' }}
      />
      <RTable<GroupedNode<T>>
        id={id}
        data={data}
        columns={columnsWithGroups}
        getRowId={getRowId}
        getSubRows={getSubRows}
        expanded={expanded}
        onExpandedChange={setExpanded}
        enableRowSelection={enableChildSelection ? canSelectRow : false}
        rowSelection={rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
        rowHeight={rowHeight}
        bodyHeight={bodyHeight}
        withColumnBorders={withColumnBorders}
        pagination={{
          current: page,
          total,
          pageSize,
          showTop: false,
          showBottom,
          // Те же варианты размера страницы, что у обычного режима.
          pageSizeOptions: [...PAGE_SIZE_OPTIONS],
          onPageSizeChange: size => controller.setPageSize(size),
        }}
        onPaginationChange={p => controller.setPage(p?.current ?? 1)}
        getRowProps={resolveRowProps}
        onRowClick={handleRowClick}
        onRowDoubleClick={handleRowDoubleClick}
        aggregateData={aggregateData}
        aggregateConfig={aggregateConfig}
        aggregatesLoading={aggregatesLoading}
        emptyPlaceholder={emptyPlaceholder}
      />
    </div>
  );
}

export default GroupedTableWrapper;
